import { computed, ref } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { OrderCardDTO } from '@/api/orders'
import { classifyCareSidebarSubBucket } from '@/api/hmCareOrderTypes'
import { lineHasConfirmedScheduleTime } from '@/api/careOrderLineDisplay'
import type { CareScheduleFocusPayload } from '../types'

export interface UseCareScheduleFocusParams {
  props: { initialFocus?: CareScheduleFocusPayload | null }
  route: RouteLocationNormalizedLoaded
  selectedOrderId: { value: string }
  findOrder: (orderId: string) => OrderCardDTO | undefined
  rescheduleLineIndexForOrder: (orderId: string) => number
}

export function useCareScheduleFocus(params: UseCareScheduleFocusParams) {
  const { props, route, selectedOrderId, findOrder, rescheduleLineIndexForOrder } = params

  const focusPayload = ref<CareScheduleFocusPayload | null>(null)
  const isRescheduleMode = computed(() => focusPayload.value?.mode === 'reschedule')

  const isCustomerRequestedReschedule = computed(() => {
    if (!isRescheduleMode.value) return false
    const kind = focusPayload.value?.rescheduleKind
    if (kind === 'customer') return true
    if (kind === 'proactive') return false
    const orderId = selectedOrderId.value || focusPayload.value?.orderId || ''
    if (!orderId) return false
    const o = findOrder(orderId)
    if (!o) return false
    const lineIndex = rescheduleLineIndexForOrder(orderId)
    const line = o.services[lineIndex]
    if (!line) return false
    const bucket = classifyCareSidebarSubBucket(o.status)
    if (bucket !== 'pend_resched' && bucket !== 'fu_pend_resched') return false
    return lineHasConfirmedScheduleTime(line, o)
  })

  /** 仅客户要求改期展示 Original Scheduled Time / Preferred Time Slots */
  const showRescheduleMetaPanel = computed(() => isCustomerRequestedReschedule.value)

  /** 优先 props.initialFocus（列表页内存传入）；否则用路由 params 兜底 */
  function resolveInitialFocus(): CareScheduleFocusPayload {
    if (props.initialFocus?.orderId) return props.initialFocus
    const paramId =
      typeof route.params.orderId === 'string' ? route.params.orderId.trim() : ''
    const qId = typeof route.query.orderId === 'string' ? route.query.orderId.trim() : ''
    return {
      orderId: paramId || qId,
      lineIndex: Number(route.query.lineIndex) || 0,
      serviceName: '',
      address: '',
      mode:
        route.name === 'HmCareOrdersReschedule'
          ? 'reschedule'
          : route.name === 'HmCareOrdersSchedule'
            ? 'schedule'
            : undefined,
    }
  }

  return {
    focusPayload,
    isRescheduleMode,
    isCustomerRequestedReschedule,
    showRescheduleMetaPanel,
    resolveInitialFocus,
  }
}
